import { Box, Stack } from "@mui/material";
import { operationsDashboardStyles } from "../styles";
import { DashboardSection } from "./DashboardSection";
import { DashboardHeader } from "./DashboardHeader";
import {
  ARCDeployedTrendItem,
  ARCNonDeployedTrendItem,
  AverageBillRateTrendItem,
  BenchCostTrendItem,
  ClientAllocatedCostTrendItem,
  SmCostTrendItem,
  CustomerMarginTrendItem,
  DirectGrossMarginTrendItem,
  EbitdaMarginTrendItem,
  GaGrossMarginTrendItem,
  InternalPlusInvestmentTrendItem,
  OperationsDashboardBuilderProps,
  OperationsDashboardVariant,
  PipelineAgingAnalysisItem,
  PipelineByOpportunityTypeItem,
  PipelineByProjectTypeItem,
  PipelineTimeSeriesItem,
  ProjectMarginTrendItem,
  RevenueTrendItem,
  UtilizationTrendItem,
  WinRatioTrendItem,
} from "../type";
import { RevenueTrendChart } from "./RevenueTrendChart";
import { ProjectMarginTrendChart } from "./ProjectMarginTrendChart";
import { DirectGrossMarginTrendChart } from "./DirectGrossMarginTrendChart";
import { CustomerMarginTrendChart } from "./CustomerMarginTrendChart";
import { EbitdaMarginTrendChart } from "./EbitdaMarginTrendChart";
import { InternalPlusInvestmentTrendChart } from "./InternalPlusInvestmentTrendChart";
import { BenchCostTrendChart } from "./BenchCostTrendChart";
import { ClientAllocatedCostTrendChart } from "./ClientAllocatedCostTrendChart";
import { ProjectAllocatedCostTrendChart } from "./ProjectAllocatedCostTrendChart";
import { ARCDeployedCostLineChart } from "./ARCDeployedLineChart";
import { ARCNonDeployedCostLineChart } from "./ARCNonDeployedLineChart";
import { AverageBillRateLineChart } from "./AverageBillRateLineChart";
import { SmCostTrendChart } from "./SmCostTrendChart";
import { GaGrossMarginTrendChart } from "./GaGrossMarginTrendChart";
import { PipelineAgingAnalysisTable } from "./L2Widgets/PipelineAgingAnalysisTable";
import { PipelineByLeadSourcesChart } from "./L2Widgets/PipelineByLeadSourcesChart";
import { DoubleDonutCategoryItem } from "./ChartTypes/GenericDoubleDonutChart";
import { PipelineByOpportunityTypeChart } from "./L2Widgets/PipelineByOpportunityTypeChart";
import { UtilizationBreakdownTrendChart } from "./L2Widgets/UtilizationBreakdownTrendChart";
import {
  UTILIZATION_CHART_OFFSHORE_TITLE,
  UTILIZATION_CHART_ONSITE_TITLE,
} from "../../../utils/consts";
import { PipelineByProjectTypeChart } from "./L2Widgets/PipelineByProjectTypeChart";
import { WinRatioChart } from "./L2Widgets/WinRatioChart";
import { PipelineTimeSeriesChart } from "./L2Widgets/PipelineTimeSeriesChart";

interface ContentProps {
  hasActiveFilters?: boolean;
  onFilterClick?: () => void;
  dashboardBuilder?: OperationsDashboardBuilderProps;
  costType?: string;
  onCostTypeChange?: (costType: string) => void;
  variant?: OperationsDashboardVariant;
  trendChartLoading?: boolean;
  revenueTrendSeries?: RevenueTrendItem[];
  projectMarginSeries?: ProjectMarginTrendItem[];
  directGrossMarginSeries?: DirectGrossMarginTrendItem[];
  clientAllocatedCostSeries?: ClientAllocatedCostTrendItem[];
  benchCostSeries?: BenchCostTrendItem[];
  internalPlusInvestmentSeries?: InternalPlusInvestmentTrendItem[];
  customerMarginSeries?: CustomerMarginTrendItem[];
  ebitdaMarginSeries?: EbitdaMarginTrendItem[];
  arcDeployedSeries?: ARCDeployedTrendItem[];
  arcNonDeployedSeries?: ARCNonDeployedTrendItem[];
  averageBillRateSeries?: AverageBillRateTrendItem[];
  smCostSeries?: SmCostTrendItem[];
  gaGrossMarginSeries?: GaGrossMarginTrendItem[];
  offshoreUtilizationSeries?: UtilizationTrendItem[];
  onsiteUtilizationSeries?: UtilizationTrendItem[];
  isBlendedRevenue?: boolean;
  pipelineByOpportunityTypeSeries?: PipelineByOpportunityTypeItem[];
  pipelineAgingAnalysisRows?: PipelineAgingAnalysisItem[];
  pipelineByLeadSourcesSeries?: DoubleDonutCategoryItem[];
  pipelineByProjectTypeSeries?: PipelineByProjectTypeItem[];
  winRatioSeries?: WinRatioTrendItem[];
  pipelineTimeSeries?: PipelineTimeSeriesItem[];
}

export const Content = ({
  hasActiveFilters = false,
  onFilterClick,
  dashboardBuilder,
  costType = "actual",
  onCostTypeChange,
  variant = "overview",
  trendChartLoading = false,
  revenueTrendSeries = [],
  projectMarginSeries = [],
  directGrossMarginSeries = [],
  clientAllocatedCostSeries = [],
  benchCostSeries = [],
  internalPlusInvestmentSeries = [],
  customerMarginSeries = [],
  ebitdaMarginSeries = [],
  arcDeployedSeries = [],
  arcNonDeployedSeries = [],
  averageBillRateSeries = [],
  smCostSeries = [],
  gaGrossMarginSeries = [],
  offshoreUtilizationSeries = [],
  onsiteUtilizationSeries = [],
  isBlendedRevenue = false,
  pipelineByOpportunityTypeSeries = [],
  pipelineAgingAnalysisRows = [],
  pipelineByLeadSourcesSeries = [],
  pipelineByProjectTypeSeries = [],
  winRatioSeries = [],
  pipelineTimeSeries = [],
}: ContentProps) => {
  const sections = dashboardBuilder?.sections || [];

  const isTrendChartVariant = variant !== "overview";
  const isRevenueTrendVariant = variant === "revenue_trend";
  const isRevenueCostAnalysis = variant === "revenue_cost_analysis";
  const isSalesPipelineHealthVariant = variant === "sales_pipeline_health";
  const isWinLossAnalysisVariant = variant === "win_loss_analysis";
  const isUtilizationAnalysis = variant === "utilization_analysis";

  let trendDetailTitle: string | undefined;
  if (isRevenueCostAnalysis) {
    trendDetailTitle = "Revenue and Cost Analysis";
  } else if (isUtilizationAnalysis) {
    trendDetailTitle = "Utilization Dashboard";
  } else if (isRevenueTrendVariant) {
    trendDetailTitle = "Revenue and Margin Detailed View";
  } else if (isSalesPipelineHealthVariant) {
    trendDetailTitle = "Sales Pipeline Health & Movement";
  } else if (isWinLossAnalysisVariant) {
    trendDetailTitle = "Win-Loss Analysis Dashboard";
  }

  const showCostToggle =
    !isSalesPipelineHealthVariant &&
    !isWinLossAnalysisVariant;

  const renderTrendCharts = () => {
    switch (variant) {
      case "revenue_cost_analysis":
        return (
          <>
            <ClientAllocatedCostTrendChart
              title="Client Allocated Cost"
              loading={trendChartLoading}
              series={clientAllocatedCostSeries}
            />
            <BenchCostTrendChart
              title="Bench Cost"
              loading={trendChartLoading}
              series={benchCostSeries}
            />
            <InternalPlusInvestmentTrendChart
              title="Internal + Investment Cost"
              loading={trendChartLoading}
              series={internalPlusInvestmentSeries}
            />
            <ProjectAllocatedCostTrendChart
              title="Project Allocated Cost"
              loading={trendChartLoading}
              series={projectMarginSeries}
            />
            <GaGrossMarginTrendChart
              title="G&A Cost"
              loading={trendChartLoading}
              series={gaGrossMarginSeries}
            />
            <SmCostTrendChart
              title="S&M Cost"
              loading={trendChartLoading}
              series={smCostSeries}
            />
            <ARCNonDeployedCostLineChart
              title="Average Resource Cost ($/hr) - Non Deployed"
              loading={trendChartLoading}
              series={arcNonDeployedSeries}
            />
            <ARCDeployedCostLineChart
              title="Average Resource Cost ($/hr) - Deployed"
              loading={trendChartLoading}
              series={arcDeployedSeries}
            />
            <AverageBillRateLineChart
              title="Average Billing Rate ($/hr)"
              loading={trendChartLoading}
              series={averageBillRateSeries}
            />
          </>
        );
      case "sales_pipeline_health":
        return (
          <>
            <PipelineByOpportunityTypeChart
              title="Pipeline by Opportunity Type"
              loading={trendChartLoading}
              series={pipelineByOpportunityTypeSeries}
            />
            <Box
              sx={{
                gridColumn: {
                  xs: "1 / -1",
                  sm: "1 / -1",
                  md: "span 2",
                },
              }}
            >
              <PipelineByLeadSourcesChart
                loading={trendChartLoading}
                categories={pipelineByLeadSourcesSeries}
              />
            </Box>
            <PipelineByProjectTypeChart
              loading={trendChartLoading}
              series={pipelineByProjectTypeSeries}
            />
            <PipelineAgingAnalysisTable
              title="Pipeline - Ageing Analysis"
              loading={trendChartLoading}
              rows={pipelineAgingAnalysisRows}
              />
            <PipelineTimeSeriesChart
              loading={trendChartLoading}
              series={pipelineTimeSeries}
            />
          </>
        );
      case "win_loss_analysis":
        return (
          <WinRatioChart
            title="Win Ratio"
            loading={trendChartLoading}
            series={winRatioSeries}
          />
        );
      case "revenue_trend":
        return (
          <>
            <RevenueTrendChart
              title="Revenue"
              loading={trendChartLoading}
              series={revenueTrendSeries}
            />
            <ProjectMarginTrendChart
              title="Project Margin"
              loading={trendChartLoading}
              series={projectMarginSeries}
            />
            <CustomerMarginTrendChart
              title="Customer Margin"
              loading={trendChartLoading}
              series={customerMarginSeries}
            />
            <DirectGrossMarginTrendChart
              title="D-GM"
              loading={trendChartLoading}
              series={directGrossMarginSeries}
            />
            <EbitdaMarginTrendChart
              title="EBITDA"
              loading={trendChartLoading}
              series={ebitdaMarginSeries}
            />
          </>
        );
      case "utilization_analysis":
        return (
          <>
            <UtilizationBreakdownTrendChart
              title={UTILIZATION_CHART_OFFSHORE_TITLE}
              loading={trendChartLoading}
              series={offshoreUtilizationSeries}
            />
            <UtilizationBreakdownTrendChart
              title={UTILIZATION_CHART_ONSITE_TITLE}
              loading={trendChartLoading}
              series={onsiteUtilizationSeries}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={operationsDashboardStyles.contentGrid}>
      <Stack spacing={0} sx={operationsDashboardStyles.headerStack}>
        <DashboardHeader
          title={trendDetailTitle}
          infoText={
            isRevenueTrendVariant && !isRevenueCostAnalysis && isBlendedRevenue
              ? "Current month revenue is shown in blended mode."
              : undefined
          }
          showLegends={!isTrendChartVariant}
          showCostToggle={showCostToggle}
          hasActiveFilters={hasActiveFilters}
          onFilterClick={onFilterClick}
          costType={costType}
          onCostTypeChange={onCostTypeChange}
        />
        {isTrendChartVariant && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 0,
            }}
          >
            {renderTrendCharts()}
          </Box>
        )}
        {!isTrendChartVariant &&
          sections.map((section) => (
            <DashboardSection
              key={section.id}
              section={section}
              fullWidth={true}
            />
          ))}
      </Stack>
    </Box>
  );
};
